import React, { useEffect, useReducer, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import Popover from "@material-ui/core/Popover";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import { PiBellSimple } from "react-icons/pi";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

import {
  Avatar,
  Badge,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
} from "@material-ui/core";
import api from "../../services/api";
import { isArray } from "lodash";
import moment from "moment";
import { getTokens } from "../../styles/tokens";
// import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const isDark = theme.palette.type === "dark";
  return {
    // o tema limita todo Popover a 300px; este painel precisa de mais largura
    popoverPaper: {
      width: "360px !important",
      maxWidth: "calc(100vw - 24px) !important",
      marginTop: 8,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      borderRadius: t.radius.md,
      backgroundColor: t.paper,
    },
    panelHeader: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "14px 16px 12px",
      borderBottom: `1px solid ${t.line}`,
    },
    panelTitle: {
      margin: 0,
      fontSize: 15,
      fontWeight: 700,
      lineHeight: "20px",
      color: t.text,
    },
    list: {
      flex: 1,
      minHeight: 0,
      maxHeight: "min(420px, calc(100vh - 160px))",
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    item: {
      width: "100%",
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      padding: "11px 16px",
      border: 0,
      borderBottom: `1px solid ${t.lineRow}`,
      background: "transparent",
      color: "inherit",
      font: "inherit",
      textAlign: "left",
      cursor: "pointer",
      transition: "background-color 150ms ease",
      "&:hover": { backgroundColor: t.muted },
      "&:focus-visible": {
        outline: `2px solid ${t.accent}`,
        outlineOffset: -2,
      },
      "&:last-child": { borderBottom: 0 },
    },
    itemAvatar: {
      flex: "none",
      width: 40,
      height: 40,
      "& svg": { fontSize: 20 },
    },
    itemBody: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    itemRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
    },
    itemTitle: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontSize: 14,
      fontWeight: 600,
      lineHeight: "20px",
      color: t.text,
    },
    itemTime: {
      flex: "none",
      fontSize: 11,
      lineHeight: "16px",
      color: t.textMuted,
      fontVariantNumeric: "tabular-nums",
    },
    itemText: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontSize: 12.5,
      lineHeight: "17px",
      color: t.textSecondary,
    },
    // prioridade do informativo: ponto colorido ao lado do título
    priorityDot: {
      flex: "none",
      width: 8,
      height: 8,
      borderRadius: "50%",
    },
    priorityHigh: { backgroundColor: t.semantic.error },
    priorityMedium: { backgroundColor: t.semantic.warning },
    priorityLow: { backgroundColor: t.textMuted },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
      padding: "32px 20px 36px",
      color: t.textSecondary,
      fontSize: 13,
      textAlign: "center",
      "& svg": {
        fontSize: 34,
        color: t.textMuted,
      },
    },
    dot: {
      backgroundColor: isDark ? "#00a884" : "#25d366",
    },
    dialogMedia: {
      display: "block",
      maxWidth: "100%",
      maxHeight: 320,
      margin: "0 auto 16px",
      borderRadius: t.radius.md,
      border: `1px solid ${t.line}`,
      objectFit: "contain",
    },
    dialogText: {
      margin: 0,
      whiteSpace: "pre-line",
      color: t.text,
      fontSize: 14,
      lineHeight: 1.55,
      overflowWrap: "anywhere",
    },
  };
});

function AnnouncementDialog({ announcement, open, handleClose }) {
  const classes = useStyles();
  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{announcement.title}</DialogTitle>
      <DialogContent>
        {announcement.mediaPath && (
          <img
            alt=""
            src={announcement.mediaPath}
            className={classes.dialogMedia}
          />
        )}
        <DialogContentText
          id="alert-dialog-description"
          className={classes.dialogText}
        >
          {announcement.text}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => handleClose()}
          color="primary"
          variant="contained"
          autoFocus
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex(
          (u) => u.id === announcement.id
        );
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement;
        } else {
          newAnnouncements.push(announcement);
        }
      });
    }

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex((u) => u.id === announcement.id);

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    } else {
      return [announcement, ...state];
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;

    const announcementIndex = state.findIndex((u) => u.id === announcementId);
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

export default function AnnouncementsPopover() {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);
  const [invisible, setInvisible] = useState(false);
  const [announcement, setAnnouncement] = useState({});
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
//   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);


  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.companyId && socket) {
      const companyId = user.companyId;
//    const socket = socketManager.GetSocket();

      const onCompanyAnnouncement = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
          setInvisible(false);
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
        }
      };
      socket.on(`company-announcement`, onCompanyAnnouncement);

      return () => {
        socket.off(`company-announcement`, onCompanyAnnouncement);
      };
    }
    return undefined;
  }, [user]);

  const fetchAnnouncements = async () => {
    try {
      // Usar nova rota que filtra por empresa
      const { data } = await api.get("/announcements/for-company", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setInvisible(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const priorityClass = (priority) => {
    if (priority === 1) return classes.priorityHigh;
    if (priority === 2) return classes.priorityMedium;
    return classes.priorityLow;
  };

  const priorityLabel = (priority) => {
    if (priority === 1) return "Prioridade alta";
    if (priority === 2) return "Prioridade média";
    return "Prioridade baixa";
  };


  const handleShowAnnouncementDialog = (record) => {
    setAnnouncement(record);
    setShowAnnouncementDialog(true);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <AnnouncementDialog
        announcement={announcement}
        open={showAnnouncementDialog}
        handleClose={() => setShowAnnouncementDialog(false)}
      />
      <IconButton
        aria-describedby={id}
        aria-label="Informativos"
        aria-haspopup="true"
        onClick={handleClick}
        color="inherit"
      >
        <Badge
          color="secondary"
          variant="dot"
          classes={{ dot: classes.dot }}
          invisible={invisible || announcements.length < 1}
        >
          <PiBellSimple />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
      >
        <div className={classes.panelHeader}>
          {/* TODO i18n */}
          <h2 className={classes.panelTitle}>Informativos</h2>
        </div>
        <div className={classes.list} onScroll={handleScroll}>
          {isArray(announcements) &&
            announcements.map((item, key) => (
              <button
                type="button"
                key={key}
                className={classes.item}
                onClick={() => handleShowAnnouncementDialog(item)}
              >
                <Avatar src={item.mediaPath || undefined} className={classes.itemAvatar}>
                  <AnnouncementIcon />
                </Avatar>
                <span className={classes.itemBody}>
                  <span className={classes.itemRow}>
                    <span
                      className={`${classes.priorityDot} ${priorityClass(item.priority)}`}
                      title={priorityLabel(item.priority)}
                    />
                    <span className={classes.itemTitle}>{item.title}</span>
                    <span className={classes.itemTime}>
                      {moment(item.createdAt).format("DD/MM/YYYY")}
                    </span>
                  </span>
                  <span className={classes.itemText}>{item.text}</span>
                </span>
              </button>
            ))}
          {isArray(announcements) && announcements.length === 0 && (
            <div className={classes.emptyState}>
              <AnnouncementIcon />
              <span>{i18n.t("mainDrawer.appBar.notRegister")}</span>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}